import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Loader2, RotateCw } from 'lucide-react'
import { LoginScreen } from '@/components/LoginScreen'
import { GuidanceCard } from '@/components/map/GuidanceCard'
import { LiveMap, type CameraRequest, type CaptureOverlay } from '@/components/map/LiveMap'
import { visibleUsers } from '@/components/map/liveLayers'
import { buildFloorOptions } from '@/components/panels/FloorSwitcher'
import { Sidebar, type ToggleKey } from '@/components/panels/Sidebar'
import { Button } from '@/components/ui/button'
import { useLiveSocket } from '@/hooks/useLiveSocket'
import { useMapData } from '@/hooks/useMapData'
import { useCaptureSession } from '@/hooks/useCaptureSession'
import { useJourneyList, useJourneyRuns } from '@/hooks/useJourneys'
import { usePersistentState } from '@/hooks/usePersistentState'
import { useSession } from '@/hooks/useSession'
import { useTheme } from '@/hooks/useTheme'
import { CAPTURE_MODES, planToGeoJSON, type CaptureModeId } from '@/lib/capture'
import { forgetUser, getLiveViewConfig } from '@/lib/api'
import { walkerUserId } from '@/lib/journeys'
import { featureLevelCounts, levelsFromFeatures } from '@/lib/style'
import { CAPTURE_ENABLED, PLACE_ID } from '@/lib/config'
import { sourceKind } from '@/lib/utils'
import type { Journey, Run } from '@/types/journey'
import type { JourneyCapabilities, LiveUser, SessionInfo } from '@/types/liveview'

type Toggles = Record<ToggleKey, boolean>

/**
 * Who is signed in decides what there is to render at all.
 *
 * The map is a separate component so that signing out unmounts it: the websocket
 * closes, the loaded style and features are dropped, and signing in as somebody
 * else starts from nothing rather than from the previous organisation's venue.
 */
export default function App() {
  const { status, session, notice, signedIn, logout } = useSession()

  if (status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
      </div>
    )
  }

  if (status === 'signed-out') {
    return <LoginScreen onSignedIn={signedIn} initialError={notice} />
  }

  return <LiveView key={session.organization.id} session={session} onLogout={logout} />
}

function LiveView({ session, onLogout }: { session: SessionInfo; onLogout: () => void }) {
  const { style, features, floors, loading, error, reload } = useMapData()
  const {
    users: userMap,
    status,
    blueiot,
    run: runEvent,
    snapshotNonce,
    reconnectNow,
  } = useLiveSocket()

  const { theme, cycleTheme } = useTheme()

  // What this deployment's journey player can do: the configured relays, and
  // per relay the three values the "Connect your app" card prints. A tag lease
  // is derived from the organisation id, so it does not move — but a relay's
  // `connected` does, so this is refetched with every snapshot, which is also
  // when a reconnected consumer B would have changed it.
  const [capabilities, setCapabilities] = useState<JourneyCapabilities | null>(null)
  useEffect(() => {
    let cancelled = false
    getLiveViewConfig()
      .then((config) => {
        if (!cancelled) setCapabilities(config.journeys ?? null)
      })
      .catch(() => {
        // A failure here only costs the relay option; previewing still works.
      })
    return () => {
      cancelled = true
    }
  }, [snapshotNonce])

  const journeyList = useJourneyList(PLACE_ID || null)
  const runs = useJourneyRuns(runEvent, snapshotNonce)
  const [selectedJourney, setSelectedJourney] = useState<Journey | null>(null)
  const [journeyFitNonce, setJourneyFitNonce] = useState(0)

  const [level, setLevel] = useState<number | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [camera, setCamera] = useState<CameraRequest | null>(null)
  const [fitNonce, setFitNonce] = useState(0)
  const [following, setFollowing] = useState(false)
  const [query, setQuery] = useState('')
  // A reload mid-walk must not cost the operator their capture setup; the
  // progress itself is already persisted by `useCaptureSession`.
  const [storedCaptureModeId, setCaptureModeId] = usePersistentState<CaptureModeId>(
    'liveview:capture:mode',
    'off',
    (value): value is CaptureModeId =>
      value === 'off' || (typeof value === 'string' && value in CAPTURE_MODES),
  )
  // Hidden capture means off, whatever a browser remembers from before.
  const captureModeId: CaptureModeId = CAPTURE_ENABLED ? storedCaptureModeId : 'off'
  const [surveyorId, setSurveyorId] = usePersistentState<string | null>(
    'liveview:capture:surveyor',
    null,
    (value): value is string | null => value === null || typeof value === 'string',
  )
  const [toggles, setToggles] = useState<Toggles>({
    showOffline: true,
    showTrails: true,
    showAccuracy: true,
    showPhones: true,
    showTags: true,
    showJourneys: true,
  })

  // A ticking clock so "12s ago" stays honest without re-rendering per frame.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const users = useMemo(
    () =>
      [...userMap.values()].sort((a, b) => {
        if (a.online !== b.online) return a.online ? -1 : 1
        // Arrival order, not last-fix order: sorting by the latest fix reshuffled
        // the list with every position update, which reads as flicker. A visitor
        // keeps its row until it goes stale; the id breaks a tie so two arrivals
        // in the same millisecond do not swap either.
        const arrived = new Date(a.first_seen).getTime() - new Date(b.first_seen).getTime()
        return arrived !== 0 ? arrived : a.user_id.localeCompare(b.user_id)
      }),
    [userMap],
  )

  const matchingUsers = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return users
    return users.filter(
      (user) =>
        user.user_id.toLowerCase().includes(needle) ||
        (user.name ?? '').toLowerCase().includes(needle),
    )
  }, [users, query])

  const featureLevels = useMemo(
    () => (features ? levelsFromFeatures(features) : []),
    [features],
  )

  const userCounts = useMemo(() => {
    const counts = new Map<number, number>()
    for (const user of users) {
      if (typeof user.level === 'number') {
        counts.set(user.level, (counts.get(user.level) ?? 0) + 1)
      }
    }
    return counts
  }, [users])

  const floorOptions = useMemo(
    () => buildFloorOptions(floors, featureLevels, userCounts),
    [floors, featureLevels, userCounts],
  )

  const levelCounts = useMemo(
    () => (features ? featureLevelCounts(features) : new Map<number, number>()),
    [features],
  )

  // Land on a floor that actually has geometry.
  //
  // Ground level is the natural default, but only if something is drawn there:
  // the org tested here has its whole venue on level -1 ("Basement") and an empty
  // level 0, so defaulting to 0 opens on a blank floor and looks broken.
  useEffect(() => {
    if (level !== null || floorOptions.length === 0) return

    const withGeometry = floorOptions.filter((option) => (levelCounts.get(option.level) ?? 0) > 0)
    const candidates = withGeometry.length > 0 ? withGeometry : floorOptions
    const chosen =
      candidates.find((option) => option.level === 0) ?? candidates[candidates.length - 1]
    setLevel(chosen.level)
  }, [floorOptions, level, levelCounts])

  const selectedUser = selectedUserId ? (userMap.get(selectedUserId) ?? null) : null

  // Who is doing the capturing. An explicit choice wins; otherwise the selected
  // device, and failing that the only device being tracked — a survey is nearly
  // always one operator walking, and making them click first is pure friction.
  //
  // Only a phone can be picked that way. A relay tag or a journey is a position
  // nobody is holding, so on a venue with one tag and no phone the auto-pick
  // would otherwise elect the tag and guide a survey from it.
  const surveyor = useMemo(() => {
    if (surveyorId) return userMap.get(surveyorId) ?? null
    if (selectedUser) return selectedUser
    const live = users.filter((user) => user.online && sourceKind(user.source) === 'phone')
    return live.length === 1 ? live[0] : null
  }, [surveyorId, userMap, selectedUser, users])

  const {
    mode: captureMode,
    plan: capturePlan,
    progress: captureProgress,
    guidance,
    reset: resetCapture,
  } = useCaptureSession({ modeId: captureModeId, features, level, surveyor })

  const captureOverlay: CaptureOverlay | null = useMemo(() => {
    if (!capturePlan || !captureMode) return null
    const { points, cells } = planToGeoJSON(capturePlan, captureMode, guidance?.id ?? null)
    return {
      points,
      cells,
      route:
        guidance && surveyor
          ? {
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: [
                      [surveyor.lng, surveyor.lat],
                      [guidance.lng, guidance.lat],
                    ],
                  },
                },
              ],
            }
          : { type: 'FeatureCollection', features: [] },
    }
  }, [capturePlan, captureMode, guidance, surveyor])

  const options = useMemo(
    () => ({ level, ...toggles, selectedUserId }),
    [level, toggles, selectedUserId],
  )

  // How many tracked devices the map is currently not drawing, so the sidebar
  // can say so instead of leaving the viewer to wonder where they went.
  const visibleCount = useMemo(() => visibleUsers(users, options).length, [users, options])
  const hiddenCount = users.length - visibleCount

  const handleLocate = useCallback((user: LiveUser) => {
    // Jumping to a device on another floor is the whole point of the button.
    if (typeof user.level === 'number') setLevel(user.level)
    setSelectedUserId(user.user_id)
    setCamera({ lng: user.lng, lat: user.lat, nonce: Date.now(), duration: 900, minZoom: 19 })
  }, [])

  const handleSelect = useCallback(
    (userId: string | null) => {
      setSelectedUserId(userId)
      if (!userId) {
        setFollowing(false)
        return
      }
      const user = userMap.get(userId)
      if (user) {
        setCamera({ lng: user.lng, lat: user.lat, nonce: Date.now(), duration: 900, minZoom: 19 })
      }
    },
    [userMap],
  )

  // Following: keep the camera on the selected device as new fixes arrive, and
  // ride along when it changes floor — a person walking up a staircase should
  // not vanish from the map.
  useEffect(() => {
    if (!following || !selectedUser) return
    if (typeof selectedUser.level === 'number' && selectedUser.level !== level) {
      setLevel(selectedUser.level)
    }
    setCamera({
      lng: selectedUser.lng,
      lat: selectedUser.lat,
      // Short, so the map keeps up with a 1 Hz feed instead of easing forever.
      nonce: Date.now(),
      duration: 400,
    })
    // Keyed on the position itself: re-centre when the device moves, not on
    // every unrelated re-render.
  }, [following, selectedUser?.lat, selectedUser?.lng, selectedUser?.level]) // eslint-disable-line react-hooks/exhaustive-deps

  // Following a device that the server has forgotten is meaningless.
  useEffect(() => {
    if (following && !selectedUser) setFollowing(false)
  }, [following, selectedUser])

  // A playing journey drags the map along — its floor above all, because a
  // walk that climbs a staircase would otherwise leave the floor being looked
  // at and simply vanish. It is always on; there is no switch any more.
  //
  // It deliberately does *not* select the walker. Selecting is what opens the
  // detail card, and starting a journey is not a request to inspect a visitor:
  // the dot is in "Current visitors" for whoever wants that. So this drives the
  // floor and the camera itself, from the walker's own fixes — the preview's
  // `journey:<run>` user, or for a relay run the leased tag as it comes back
  // through the loopback. Either exists only once the first frame has landed.
  //
  // With several walks playing, only one is followed: two walkers on two floors
  // would flip the map between them every fix. It is the most recently
  // (re)started one that is actually moving — a paused walk goes nowhere — and
  // `started_at` is when a run last entered `running`, so resuming an older
  // walk hands the map to it, which is the one the operator just touched.
  const followedRun = useMemo(() => {
    const live = runs.runs.filter((run) => run.state === 'running' || run.state === 'paused')
    const byStart = (a: Run, b: Run) =>
      new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
    const running = live.filter((run) => run.state === 'running').sort(byStart)
    return running[0] ?? live.sort(byStart)[0] ?? null
  }, [runs.runs])
  const runWalkerId = followedRun ? walkerUserId(followedRun) : null
  const runWalker = runWalkerId ? (userMap.get(runWalkerId) ?? null) : null

  useEffect(() => {
    // A device the operator chose to follow wins until they let it go.
    if (following || !runWalker) return
    if (typeof runWalker.level === 'number' && runWalker.level !== level) {
      setLevel(runWalker.level)
    }
    setCamera({ lng: runWalker.lng, lat: runWalker.lat, nonce: Date.now(), duration: 400 })
    // Keyed on the walker's position, like the effect above.
  }, [following, runWalker?.user_id, runWalker?.lat, runWalker?.lng, runWalker?.level]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleForget = useCallback(
    (userId: string) => {
      forgetUser(userId).catch(() => {
        // The websocket is the source of truth; a failed delete simply leaves
        // the device on the map until the server expires it on its own.
      })
      setSelectedUserId(null)
    },
    [],
  )

  const handleToggle = useCallback((key: ToggleKey, value: boolean) => {
    setToggles((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleFit = useCallback(() => setFitNonce((nonce) => nonce + 1), [])

  // Picking a journey draws it and frames it — a multi-floor walk is rarely
  // inside the view you happened to be looking at.
  const handleSelectJourney = useCallback((journey: Journey | null) => {
    setSelectedJourney(journey)
    if (journey) setJourneyFitNonce((nonce) => nonce + 1)
  }, [])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" />
          Loading Proximi.io style and features…
        </div>
      </div>
    )
  }

  if (error || !style || !features) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="border-destructive/40 bg-card max-w-lg rounded-xl border p-6">
          <div className="text-destructive flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="size-4" />
            Could not load the map
          </div>
          <p className="text-muted-foreground mt-2 font-mono text-xs break-all">
            {error ?? 'The API returned no style.'}
          </p>
          <p className="text-muted-foreground mt-3 text-xs">
            The style, features and floors are all fetched through <code>/v1/proximi/…</code> with
            your own Proximi.io token, so this usually means the API cannot reach the platform — or
            that this organisation has no map to draw.
          </p>
          <Button variant="outline" size="sm" className="mt-4" onClick={reload}>
            <RotateCw />
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full">
      <Sidebar
        status={status}
        onReconnect={reconnectNow}
        relay={blueiot}
        session={session}
        onLogout={onLogout}
        floorOptions={floorOptions}
        level={level}
        onLevelChange={setLevel}
        users={matchingUsers}
        onlineCount={users.filter((user) => user.online).length}
        selectedUser={selectedUser}
        selectedUserId={selectedUserId}
        onSelectUser={handleSelect}
        onLocate={handleLocate}
        onForget={handleForget}
        now={now}
        showOffline={toggles.showOffline}
        showTrails={toggles.showTrails}
        showAccuracy={toggles.showAccuracy}
        showPhones={toggles.showPhones}
        showTags={toggles.showTags}
        showJourneys={toggles.showJourneys}
        onToggle={handleToggle}
        query={query}
        onQueryChange={setQuery}
        onFitToDevices={handleFit}
        visibleCount={visibleCount}
        following={following}
        onToggleFollow={() => setFollowing((previous) => !previous)}
        theme={theme}
        onCycleTheme={cycleTheme}
        hiddenCount={hiddenCount}
        captureModeId={captureModeId}
        onCaptureModeChange={setCaptureModeId}
        surveyorId={surveyor?.user_id ?? null}
        onSurveyorChange={setSurveyorId}
        captureProgress={captureProgress}
        onCaptureReset={resetCapture}
        journeys={{
          journeys: journeyList.journeys,
          total: journeyList.total,
          loading: journeyList.loading,
          error: journeyList.error,
          onReload: journeyList.reload,
          selected: selectedJourney,
          onSelect: handleSelectJourney,
          capabilities,
          runs: runs.runs,
          runError: runs.error,
          busy: runs.busy,
          // No ground floor: the run takes its relay's default, the number
          // "Connect your app" prints.
          onStart: ({ journeyId, sink, relay, walker, rateHz, speed, loop }) =>
            runs.start({
              journey_id: journeyId,
              sink,
              relay,
              walker,
              rate_hz: rateHz,
              speed,
              loop,
            }),
          onControl: runs.control,
          onDismissError: runs.clearError,
          now,
        }}
      />
      <main className="relative min-w-0 flex-1">
        <LiveMap
          style={style}
          features={features}
          users={users}
          options={options}
          camera={camera}
          fitNonce={fitNonce}
          capture={captureOverlay}
          journey={selectedJourney}
          journeyFitNonce={journeyFitNonce}
          onSelectUser={handleSelect}
        />
        {captureMode && (
          <GuidanceCard
            mode={captureMode}
            progress={captureProgress}
            guidance={guidance}
            waitingForFix={!surveyor}
          />
        )}
      </main>
    </div>
  )
}
